import React, { useState, useEffect } from "react";
import { api } from "../utils/api";
import { PageHeader } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { StatCard } from "../components/ui/StatCard";
import { TableCard, td, tdMuted, trStyle } from "../components/ui/Table";
import { EmptyState, LoadingRow } from "../components/ui/EmptyState";
import { colors } from "../components/ui/tokens";

const IconEuro = ({ color }: { color: string }) => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>;
const IconLock = ({ color }: { color: string }) => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>;
const IconCard = ({ color }: { color: string }) => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>;

const STATUS_TONE: Record<string, "success" | "warning" | "danger" | "neutral" | "accent"> = {
  completed: "success", succeeded: "success", paid: "success", releasing: "success",
  pending: "neutral", processing: "neutral", awaiting_payout_setup: "neutral", pending_payment: "neutral",
  disputed: "warning",
  failed: "danger", refunded: "danger", cancelled: "danger",
};
const STATUS_LABEL: Record<string, string> = {
  succeeded: "En séquestre", awaiting_payout_setup: "En attente de virement", releasing: "Virement en cours",
  pending_payment: "Paiement non finalisé",
  disputed: "En attente (litige)", refunded: "Remboursé", failed: "Échec", cancelled: "Annulé",
};

// Un paiement en 'succeeded'/'awaiting_payout_setup' peut être mis en attente (suite à un
// signalement ailleurs dans l'app) ou remboursé directement ; un paiement déjà 'disputed' peut
// être remboursé ou voir sa suspension levée. Un paiement resté en 'pending_payment' (checkout
// Stripe abandonné, jamais confirmé par le webhook) n'a jamais été encaissé — rien à rembourser,
// juste à annuler pour le sortir de la liste. Voir OLTODO-backend/src/routes/payments.ts.
const canHold = (p: any) => ["succeeded", "awaiting_payout_setup"].includes(p.status);
const canRefund = (p: any) => ["succeeded", "awaiting_payout_setup", "disputed"].includes(p.status);
const canRelease = (p: any) => p.status === "disputed";
const canCancel = (p: any) => p.status === "pending_payment";

export default function Payments() {
  const [payments, setPayments] = useState<any[]>([]);
  const [stats, setStats] = useState({ total_revenue: 0, in_escrow: 0, transactions_count: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = () => {
    api.get("/payments").then(data => {
      if (data?.payments) setPayments(data.payments);
      if (data?.stats) setStats(data.stats);
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const hold = (p: any) => {
    const reason = window.prompt("Motif de la mise en attente (envoyé à l'utilisateur en interne) :");
    if (reason === null) return;
    setBusy(p.id);
    api.put(`/payments/${p.id}/hold`, { reason }).then(data => {
      if (data?.error) { alert(data.error); return; }
      load();
    }).catch(() => {}).finally(() => setBusy(null));
  };

  const refund = (p: any) => {
    if (!window.confirm(`Rembourser ${Number(p.amount).toFixed(2)} € à ${p.user_name} ? Action irréversible.`)) return;
    setBusy(p.id);
    api.post(`/payments/${p.id}/resolve-dispute`, { action: "refund" }).then(data => {
      if (data?.error) { alert(data.error); return; }
      load();
    }).catch(() => {}).finally(() => setBusy(null));
  };

  const release = (p: any) => {
    if (!window.confirm("Lever la suspension et laisser le virement reprendre son cours ?")) return;
    setBusy(p.id);
    api.post(`/payments/${p.id}/resolve-dispute`, { action: "release" }).then(data => {
      if (data?.error) { alert(data.error); return; }
      load();
    }).catch(() => {}).finally(() => setBusy(null));
  };

  const cancel = (p: any) => {
    if (!window.confirm(`Annuler ce paiement non finalisé de ${p.user_name} ? Aucun montant n'a été prélevé.`)) return;
    setBusy(p.id);
    api.put(`/payments/${p.id}/cancel`, {}).then(data => {
      if (data?.error) { alert(data.error); return; }
      load();
    }).catch(() => {}).finally(() => setBusy(null));
  };

  return (
    <div style={{ padding: 32 }}>
      <PageHeader title="Paiements" subtitle="Transactions et séquestres" />

      <div style={{ display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
        <StatCard label="Revenus totaux" value={`${Number(stats.total_revenue).toFixed(2)} €`} tone="success" icon={<IconEuro color={colors.success} />} />
        <StatCard label="En séquestre" value={`${Number(stats.in_escrow).toFixed(2)} €`} tone="warning" icon={<IconLock color={colors.warning} />} />
        <StatCard label="Transactions" value={stats.transactions_count} tone="accent" icon={<IconCard color={colors.accent} />} />
      </div>

      <TableCard headers={["Utilisateur", "Concerne", "Montant", "Statut", "Séquestre", "Date", "Actions"]}>
        {loading ? (
          <tr><td colSpan={7}><LoadingRow /></td></tr>
        ) : payments.length === 0 ? (
          <tr><td colSpan={7}><EmptyState title="Aucune transaction pour le moment" icon={<IconCard color={colors.textFaint} />} /></td></tr>
        ) : payments.map(p => (
          <tr key={p.id} style={trStyle}>
            <td style={{ ...td, fontWeight: 600 }}>{p.user_name}</td>
            <td style={tdMuted}>{p.target_label || "—"}</td>
            <td style={{ ...td, fontWeight: 700 }}>{Number(p.amount).toFixed(2)} €</td>
            <td style={td}>
              <Badge tone={STATUS_TONE[p.status] || "neutral"}>{STATUS_LABEL[p.status] || p.status}</Badge>
              {p.status === "disputed" && p.dispute_reason && <div style={{ fontSize: 11, color: colors.textFaint, marginTop: 4, maxWidth: 180 }}>« {p.dispute_reason} »</div>}
            </td>
            <td style={{ ...td, color: colors.textFaint, fontSize: 12.5 }}>{p.escrow_release_at ? new Date(p.escrow_release_at).toLocaleDateString("fr-FR") : "—"}</td>
            <td style={{ ...td, color: colors.textFaint, fontSize: 12.5 }}>{new Date(p.created_at).toLocaleDateString("fr-FR")}</td>
            <td style={td}>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {canHold(p) && <Button small variant="secondary" disabled={busy === p.id} onClick={() => hold(p)}>Mettre en attente</Button>}
                {canRelease(p) && <Button small variant="secondary" style={{ backgroundColor: colors.successSoft, color: colors.success }} disabled={busy === p.id} onClick={() => release(p)}>Lever la suspension</Button>}
                {canRefund(p) && <Button small variant="danger" disabled={busy === p.id} onClick={() => refund(p)}>Rembourser</Button>}
                {canCancel(p) && <Button small variant="danger" disabled={busy === p.id} onClick={() => cancel(p)}>Annuler</Button>}
                {!canHold(p) && !canRefund(p) && !canRelease(p) && !canCancel(p) && <span style={{ color: colors.textFaint, fontSize: 12 }}>—</span>}
              </div>
            </td>
          </tr>
        ))}
      </TableCard>
    </div>
  );
}
